import { useEffect, useRef, type ReactNode, type RefObject } from "react";
import { IconButton } from "./icon_button";

export interface ModalProps {
  readonly children: ReactNode;
  readonly descriptionId?: string;
  readonly initialFocusRef?: RefObject<HTMLElement | null> | undefined;
  readonly onClose: () => void;
  readonly title: string;
  readonly titleId: string;
}

/** Renders a focused, keyboard-contained shell dialog with deterministic restoration. */
export function Modal({
  children,
  descriptionId,
  initialFocusRef,
  onClose,
  title,
  titleId,
}: ModalProps) {
  const dialogRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const previouslyFocused = document.activeElement;
    const dialog = dialogRef.current;
    (initialFocusRef?.current ?? dialog)?.focus();

    /** Closes on Escape and cycles Tab within the dialog. */
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
        return;
      }
      if (event.key !== "Tab" || dialog === null) {
        return;
      }
      const focusable = getFocusableElements(dialog);
      if (focusable.length === 0) {
        event.preventDefault();
        dialog.focus();
        return;
      }
      const first = focusable[0];
      const last = focusable.at(-1);
      if (first === undefined || last === undefined) {
        return;
      }
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      if (previouslyFocused instanceof HTMLElement && previouslyFocused.isConnected) {
        previouslyFocused.focus();
      }
    };
  }, [initialFocusRef, onClose]);

  return (
    <div className="modal-backdrop">
      <section
        ref={dialogRef}
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
        tabIndex={-1}
      >
        <header className="modal__header">
          <h2 id={titleId}>{title}</h2>
          <IconButton
            className="icon-button"
            label="Close dialog"
            tooltipPlacement="left"
            onClick={onClose}
          >
            ×
          </IconButton>
        </header>
        {children}
      </section>
    </div>
  );
}

/** Finds enabled controls in document order for the modal focus loop. */
function getFocusableElements(container: HTMLElement): readonly HTMLElement[] {
  return [
    ...container.querySelectorAll<HTMLElement>("button, input, select, textarea, [href]"),
  ].filter((element) => !element.hasAttribute("disabled") && element.tabIndex !== -1);
}
